import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import JoinPage from "@/pages/JoinPage";
import FeedPage from "@/pages/FeedPage";
import AlbumsPage from "@/pages/AlbumsPage";
import AlbumDetailPage from "@/pages/AlbumDetailPage";
import SmartCollectionPage from "@/pages/SmartCollectionPage";
import CalendarPage from "@/pages/CalendarPage";
import FamilyPage from "@/pages/FamilyPage";
import ProfilePage from "@/pages/ProfilePage";
import CreateFamilyPage from "@/pages/CreateFamilyPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import PricingPage from "@/pages/PricingPage";
import LandingPage from "@/pages/LandingPage";
import LLMInfoPage from "@/pages/LLMInfoPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ReportsPage from "@/pages/ReportsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import TreePage from "@/pages/TreePage";
import AdminActivityPage from "@/pages/AdminActivityPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AdminPushPage from "@/pages/AdminPushPage";
import AdminFeedbackPage from "@/pages/AdminFeedbackPage";
import FeedbackPage from "@/pages/FeedbackPage";
import OrdersPage from "@/pages/OrdersPage";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PushPermissionPrompt from "@/components/PushPermissionPrompt";
import UpgradePromptModal from "@/components/UpgradePromptModal";
import ProactiveUpsellModal from "@/components/ProactiveUpsellModal";
import { shouldHidePWAPrompt, shouldHideSubscriptionCTAs } from "@/lib/platform";
import { teardownIOSPushNotifications } from "@/lib/pushNotificationsIOS";
import { guardAgainstStaleBundle } from "@/lib/iosStaleBundleGuard";
import { hideSplashWithFade } from "@/lib/splashScreen";
import { useEffect, useRef } from "react";
import OnboardingWizard from "@/components/OnboardingWizard";
import ErrorBoundary from "@/components/ErrorBoundary";

// iOS push registration is USER-INITIATED only.
//
// Earlier versions ran `initIOSPushNotifications()` in a bootstrap effect
// on every login. That was the root cause of the "toggle stuck on
// checking…" TestFlight bug: the bootstrap grabbed the module-level
// `_initializationPromise` guard, and if the native call hung during
// cold-start (which it can on iOS 17+ under certain permission states),
// the user's toggle tap would be deduped back to that stalled promise
// — never binding `_activeOnStep` to the caller's diagnostics callback,
// so the UI updates never fired.
//
// The fix: don't touch the guard from bootstrap at all. Registration
// happens only when the user explicitly taps the toggle or the manual
// "Request iOS Notification Permission" button in ProfilePage.
//
// Teardown ONLY runs on a real authenticated→unauthenticated transition.
// Specifically we must NOT call teardown during:
//   1. Initial mount while `AuthProvider` is still resolving the session
//      cookie (`loading === true`) — the token exists on the backend but
//      React hasn't hydrated `user` yet. Calling teardown here would
//      delete the freshly-registered APNs token belonging to a user who
//      is about to appear seconds later.
//   2. The first render after cold-start when `user` is temporarily
//      `null` before the auth request completes.
// Tracking `previousUserId.current` lets us detect the specific
// "had a user, now don't" edge without firing on the initial mount.
function IOSPushBootstrap() {
  const { user, loading } = useAuth();
  const previousUserId = useRef(null);
  useEffect(() => {
    // Stale-bundle guard runs unconditionally on mount for native iOS —
    // it's a no-op on every other platform. Runs alongside the auth
    // hydration so a reload (if the production commit changed) fires
    // before the user has typed anything.
    guardAgainstStaleBundle();
  }, []);
  useEffect(() => {
    // Auth is still hydrating — user may transiently be null; do nothing.
    if (loading) return;
    const hadUser = Boolean(previousUserId.current);
    const hasUser = Boolean(user?.id);
    // Only teardown on the specific authenticated → unauthenticated
    // transition (i.e. a real logout / account deletion / 401 kick-out).
    if (hadUser && !hasUser) {
      teardownIOSPushNotifications();
    }
    previousUserId.current = user?.id || null;
  }, [loading, user?.id]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-teal-600 font-medium">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function RequireFamily({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-teal-600 font-medium">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!user.family_id) {
    return <Navigate to="/create-family" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-teal-600 font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/llm-info" element={<LLMInfoPage />} />
      <Route path="/create-family" element={
        <ProtectedRoute>
          {user?.family_id ? <Navigate to="/" replace /> : <CreateFamilyPage />}
        </ProtectedRoute>
      } />
      <Route path="/" element={
        user ? (
          user.family_id ? <FeedPage /> : <Navigate to="/create-family" replace />
        ) : (
          <LandingPage />
        )
      } />
      <Route path="/app" element={
        <RequireFamily>
          <FeedPage />
        </RequireFamily>
      } />
      <Route path="/albums" element={
        <RequireFamily>
          <AlbumsPage />
        </RequireFamily>
      } />
      <Route path="/albums/:albumId" element={
        <RequireFamily>
          <AlbumDetailPage />
        </RequireFamily>
      } />
      <Route path="/collections/:theme" element={
        <RequireFamily>
          <SmartCollectionPage />
        </RequireFamily>
      } />
      <Route path="/calendar" element={
        <RequireFamily>
          <CalendarPage />
        </RequireFamily>
      } />
      <Route path="/events/:eventId" element={
        <RequireFamily>
          <EventDetailPage />
        </RequireFamily>
      } />
      <Route path="/tree" element={
        <RequireFamily>
          <TreePage />
        </RequireFamily>
      } />
      <Route path="/family" element={
        <RequireFamily>
          <FamilyPage />
        </RequireFamily>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/moderation" element={
        <RequireFamily>
          <ReportsPage />
        </RequireFamily>
      } />
      <Route path="/admin/activity" element={
        <ProtectedRoute>
          <AdminActivityPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute>
          <AdminAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/push" element={
        <ProtectedRoute>
          <AdminPushPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedRoute>
          <AdminFeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <FeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <OrdersPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function SplashHider() {
  useEffect(() => {
    // Fire once — internal guard makes repeat calls safe.
    hideSplashWithFade();
  }, []);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <IOSPushBootstrap />
          <SplashHider />
          {!shouldHidePWAPrompt() && <PWAInstallPrompt />}
          <PushPermissionPrompt />
          {!shouldHideSubscriptionCTAs() && <UpgradePromptModal />}
          {!shouldHideSubscriptionCTAs() && <ProactiveUpsellModal />}
          <OnboardingWizard />
          <Toaster position="top-center" offset="calc(env(safe-area-inset-top, 0px) + 108px)" />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
